import { useLayoutEffect, useRef, useState } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'

export interface SeedableQuery<T> {
  data: T | undefined
  refetch: () => Promise<{ data?: T }>
}

export interface ServerSeedOptions<T> {
  /** Copies server data into the screen's editable state. */
  seed: (data: T) => void
  /** Whether the screen holds unsaved edits right now. */
  isDirty: () => boolean
}

export interface ServerSeed<T> {
  /** The server data changed under unsaved edits: Save must not run. */
  baseChanged: boolean
  /** Re-seed from the current server data, dropping unsaved edits. */
  reload: () => void
  /** After a successful save: `payload` is what the server now holds. */
  noteSaved: (payload: T) => void
  /** Refetch before a save; false (and `baseChanged`) if the data changed. */
  ensureCurrent: () => Promise<boolean>
}

// For a screen that edits a local copy of server data (draft + savedSnapshot):
// seeds that copy, and re-seeds it whenever the server data it was built
// from changes - a Diff & Validation discard, a revert, publish, another tab
// - unless the user has unsaved edits, which are never wiped; those block
// Save instead (`baseChanged`) until the user reloads.
//
// "Changed" is the query data's identity, not a comparison of edits: React
// Query's structural sharing keeps the same object when a refetch returns
// equal JSON, so a refetch on window focus or after an unrelated write is
// not a change. Different from hooks/useSeededModalField.ts, which seeds a
// modal once per open cycle from the data at open time.
export function useServerSeed<T>(
  queryKey: QueryKey,
  query: SeedableQuery<T>,
  { seed, isDirty }: ServerSeedOptions<T>,
): ServerSeed<T> {
  const queryClient = useQueryClient()
  const [seededFrom, setSeededFrom] = useState<T | undefined>(undefined)
  const seededFromRef = useRef<T | undefined>(undefined)
  useLayoutEffect(() => {
    seededFromRef.current = seededFrom
  })

  const data = query.data
  const changed = data !== undefined && data !== seededFrom
  // Safe conditional setState-during-render, the same pattern the screens
  // used for their one-time seed.
  if (changed && (seededFrom === undefined || !isDirty())) {
    seed(data)
    setSeededFrom(data)
  }

  return {
    baseChanged: changed && seededFrom !== undefined,
    reload: () => {
      if (data === undefined) return
      seed(data)
      setSeededFrom(data)
    },
    noteSaved: (payload) => {
      const stored = queryClient.setQueryData<T>(queryKey, payload)
      setSeededFrom(stored)
      seededFromRef.current = stored
    },
    ensureCurrent: async () => {
      if (seededFromRef.current === undefined) return true
      const result = await query.refetch()
      return result.data === undefined || result.data === seededFromRef.current
    },
  }
}
